import React from 'react';
import { Leg } from 'acscp/store';
import { DurationStyle } from 'acscp/components/common/helpers/formatMoments';
import style from './flightDetailsLegTable.module.scss';
import classNames from 'classnames';
import { FormattedMessage, InjectedIntl, InjectedIntlProps, injectIntl } from 'react-intl';
import { Duration } from 'acscp/components/common/duration';
import { LabeledItem } from 'acscp/components/common/labeledItem';
import { addZeroWidthSpacesAroundSeparatorCharacters } from 'acscp/components/common/helpers/stringHelpers';
import { LocalTimeWithFallback } from 'acscp/components/common/localTimeWithFallback';
import { DateStyle, FormattedDate } from 'acscp/components/common/formattedDate';
import { DateType } from 'acscp/api/models/customTypes/dateType';
import { TimeType } from 'acscp/api/models/customTypes/timeType';
import { FormattedDateRange } from 'acscp/components/common/formattedDateRange';
import { Department } from 'acscp/api/models/customTypes/department';
import { QuoteType } from 'acscp/api/models/customTypes/quoteType';
import MessageDescriptor = ReactIntl.FormattedMessage.MessageDescriptor;

const shouldShowArrivalDateFor = (leg: Leg): boolean =>
    leg.departureDateType === DateType.EXACT
    && leg.departureDate.diff(leg.arrivalDate, /*inDays*/ true) !== 0;
const shouldShowArrivalTimeFor = (leg: Leg): boolean => leg.isArrivalTimeSpecified;
const shouldShowFlightDurationFor = (leg: Leg): boolean => !!leg.flightTimeDuration;
const shouldShowPetsFor = (leg: Leg): boolean => !!leg.numberOfPets && leg.numberOfPets > 0;

const FlightDetailsLegTablePresentational: React.SFC<{
    legs: Leg[],
    className?: string,
    hasLowerBorder: boolean,
    tableHeader?: MessageDescriptor,
    departmentKey: Department,
    quoteType: QuoteType
} & InjectedIntlProps> = props => {
    const {departmentKey, quoteType} = props;
    const shouldShowArrivalDateColumn = props.legs.some(shouldShowArrivalDateFor);
    const shouldShowArrivalTimeColumn = props.legs.some(shouldShowArrivalTimeFor);
    const shouldShowFlightDurationColumn = props.legs.some(shouldShowFlightDurationFor);
    const shouldShowPetsColumn = departmentKey === Department.PJETS && props.legs.some(shouldShowPetsFor);

    return (
        <div className={props.className}>
            <table
                className={classNames(
                    style.FlightDetailsLegTable,
                    {[style.HasLowerBorder]: props.hasLowerBorder},
                    {[style.HasArrivalTimes]: shouldShowArrivalTimeColumn})
                }
            >
                <tbody>
                    <tr>
                        <th>
                            {props.tableHeader
                            ?
                                <>
                                    {props.intl.formatMessage(props.tableHeader)}
                                </>
                            :
                                <FormattedMessage
                                    id={'components.legs.flightDetailsLegTable.flight'}
                                    defaultMessage={'FLIGHT'}
                                />
                            }

                        </th>
                        {shouldShowArrivalDateColumn
                            ?
                            <th>
                                <FormattedMessage
                                    id={'components.legs.flightDetailsLegTable.departureDate'}
                                    defaultMessage={'DEPARTURE DATE'}
                                />
                            </th>
                            :
                            <th>
                                <FormattedMessage
                                    id={'components.legs.flightDetailsLegTable.date'}
                                    defaultMessage={'DATE'}
                                />
                            </th>
                        }
                        <th>
                            {props.legs.length > 0 && (props.legs[0].departureTimeType === TimeType.UTC
                                ? <FormattedMessage
                                    id={'components.legs.flightDetailsLegTable.departUTC'}
                                    defaultMessage={'DEPART (UTC)'}
                                />
                                : <FormattedMessage
                                    id={'components.legs.flightDetailsLegTable.departLocal'}
                                    defaultMessage={`DEPART (LOCAL)`}
                                />)
                            }
                        </th>
                        {shouldShowArrivalDateColumn && (
                            <th>
                                <FormattedMessage
                                    id={'components.legs.flightDetailsLegTable.arrivalDate'}
                                    defaultMessage={'ARRIVAL DATE'}
                                />
                            </th>
                        )}
                        {shouldShowArrivalTimeColumn &&
                            <th>
                                {props.legs.length > 0 && (props.legs[0].arrivalTimeType === TimeType.UTC
                                    ? <FormattedMessage
                                        id={'components.legs.flightDetailsLegTable.arriveUTC'}
                                        defaultMessage={'ARRIVE (UTC)'}
                                    />
                                    : <FormattedMessage
                                        id={'components.legs.flightDetailsLegTable.arriveLocal'}
                                        defaultMessage={'ARRIVE (LOCAL)'}
                                    />)
                                }
                            </th>
                        }
                        {shouldShowFlightDurationColumn &&
                            <th>
                                {quoteType === QuoteType.GoNow
                                    ? <FormattedMessage
                                        id={'components.legs.flightDetailsLegTable.flightTime'}
                                        defaultMessage={'FLIGHT TIME'}
                                    />
                                    : <FormattedMessage
                                        id={'components.legs.flightDetailsLegTable.duration'}
                                        defaultMessage={'DURATION'}
                                    />
                                }
                            </th>
                        }
                        {departmentKey !== Department.Cargo &&
                            <th>
                                <FormattedMessage
                                    id={'components.legs.flightDetailsLegTable.passengers'}
                                    defaultMessage={'PASSENGERS'}
                                />
                            </th>
                        }
                        {departmentKey === Department.Cargo &&
                            <th>
                                <FormattedMessage
                                    id={'components.legs.flightDetailsLegTable.cargoWeight'}
                                    defaultMessage={'CARGO WEIGHT'}
                                />
                            </th>
                        }
                        {shouldShowPetsColumn &&
                            <th>
                                <FormattedMessage
                                    id={'components.legs.flightDetailsLegTable.pets'}
                                    defaultMessage={'PETS'}
                                />
                            </th>
                        }
                    </tr>
                    {props.legs.map((leg, index) =>
                        (LegRow(
                            leg,
                            index,
                            shouldShowArrivalDateColumn,
                            shouldShowArrivalTimeColumn,
                            shouldShowFlightDurationColumn,
                            departmentKey,
                            quoteType,
                            shouldShowPetsColumn,
                        )))
                    }
                </tbody>
            </table>
            <div className={classNames(style.FlightDetailsMobileView, {[style.HasLowerBorder]: props.hasLowerBorder})}>
                {props.legs.map((leg, index) => (
                    LegSection(props.tableHeader, props.intl, leg, index, departmentKey, quoteType, shouldShowPetsColumn))
                )}
            </div>
        </div>
    );
};

function LegRow(leg: Leg,
                index: number,
                shouldShowArrivalDateColumn: boolean,
                shouldShowArrivalTimeColumn: boolean,
                shouldShowFlightDurationColumn: boolean,
                departmentKey: Department,
                quoteType: QuoteType,
                shouldShowPetsColumn: boolean): JSX.Element {
    const dateFormat = quoteType === QuoteType.GoNow
        ? DateStyle.Medium
        : DateStyle.Long;
    return (
        <tr key={index}>
            <td>
                <div>
                    {addZeroWidthSpacesAroundSeparatorCharacters(leg.departureLocation)}
                    {' - '}
                    {addZeroWidthSpacesAroundSeparatorCharacters(leg.arrivalLocation)}
                </div>
                {leg.isFuelStopRequested &&
                    <div className={style.FuelStopInfo}>
                        <span
                            className={classNames(
                                style.IncludesFuelStopText,
                                {[style.IncludesFuelStopTextCargo]: departmentKey === Department.Cargo})}
                        >
                            {'*'}
                            <FormattedMessage
                                id={'components.legs.flightDetailsLegTable.includesFuelStop'}
                                defaultMessage={'Includes Fuel Stop(s)'}
                            />
                        </span>
                        {leg.fuelStopText && ' - ' + leg.fuelStopText}
                    </div>
                }
            </td>
            <td><FormattedDateRange date={leg.departureDate} rangeEndDate={leg.departureRangeEndDate} style={dateFormat}/></td>
            <td>
                <LocalTimeWithFallback dateTime={leg.departureDate} />
            </td>
            {shouldShowArrivalDateColumn && (
                <td>{leg.departureDateType === DateType.EXACT && <FormattedDate date={leg.arrivalDate} style={dateFormat}/>}</td>
            )}
            {shouldShowArrivalTimeColumn &&
                <td><LocalTimeWithFallback dateTime={leg.arrivalDate} /></td>
            }
            {shouldShowFlightDurationColumn &&
                <td><Duration duration={leg.flightTimeDuration} style={DurationStyle.Minimal}/></td>
            }
            {departmentKey !== Department.Cargo &&
                <td>{leg.numberOfPassengers}</td>
            }
            {departmentKey === Department.Cargo &&
                <td>{leg.cargoWeight}</td>
            }
            {shouldShowPetsColumn &&
                <td>{leg.numberOfPets}</td>
            }
        </tr>
    );
}

function LegSection(
    tableHeader: MessageDescriptor|undefined,
    intl: InjectedIntl,
    leg: Leg,
    index: number,
    departmentKey: Department,
    quoteType: QuoteType,
    shouldShowPetsColumn: boolean,
): JSX.Element {
    const dateFormat = quoteType === QuoteType.GoNow
        ? DateStyle.Medium
        : DateStyle.Long;
    return (
        <div className={style.FlightDetailsLegSection} key={index}>
            <div className={style.Airports}>
                {tableHeader
                    ? <>
                        {intl.formatMessage(tableHeader)}:&nbsp;
                    </>
                    : null}
                {addZeroWidthSpacesAroundSeparatorCharacters(leg.departureLocation)}
                {' - '}
                {addZeroWidthSpacesAroundSeparatorCharacters(leg.arrivalLocation)}
            </div>
            {leg.isFuelStopRequested &&
                <div className={style.FuelStopInfo}>
                    <span
                        className={classNames(
                            style.IncludesFuelStopText,
                            {[style.IncludesFuelStopTextCargo]: departmentKey === Department.Cargo})}
                    >
                        {'*'}
                        <FormattedMessage
                            id={'components.legs.flightDetailsLegTable.includesFuelStop'}
                            defaultMessage={'Includes Fuel Stop(s)'}
                        />
                    </span>
                    {leg.fuelStopText && ' - ' + leg.fuelStopText}
                </div>
            }

            <LabeledItem
                className={style.MobileLegInfo}
                label={
                    <FormattedMessage
                        id={'components.legs.flightDetailsLegTable.departureDateMobile'}
                        defaultMessage={'Departure date'}
                    />
                }
            >
                <FormattedDateRange date={leg.departureDate} rangeEndDate={leg.departureRangeEndDate} style={dateFormat}/>
            </LabeledItem>

            <LabeledItem
                className={style.MobileLegInfo}
                label={leg.departureTimeType === TimeType.UTC
                    ? <FormattedMessage
                        id={'components.legs.flightDetailsLegTable.departUTCMobile'}
                        defaultMessage={'Departure (UTC)'}
                    />
                    : <FormattedMessage
                        id={'components.legs.flightDetailsLegTable.departLocalMobile'}
                        defaultMessage={'Departure (local)'}
                    />
                }
            >
                <LocalTimeWithFallback dateTime={leg.departureDate} />
            </LabeledItem>

            {shouldShowArrivalDateFor(leg) &&
                <LabeledItem
                    className={style.MobileLegInfo}
                    label={
                        <FormattedMessage
                            id={'components.legs.flightDetailsLegTable.arrivalDateMobile'}
                            defaultMessage={'Arrival date'}
                        />
                    }
                >
                    <FormattedDate date={leg.arrivalDate} style={dateFormat}/>
                </LabeledItem>
            }
            {shouldShowArrivalTimeFor(leg) &&
                <LabeledItem
                    className={style.MobileLegInfo}
                    label={leg.arrivalTimeType === TimeType.UTC
                        ? <FormattedMessage
                            id={'components.legs.flightDetailsLegTable.arriveUTCMobile'}
                            defaultMessage={'Arrival (UTC)'}
                        />
                        : <FormattedMessage
                            id={'components.legs.flightDetailsLegTable.arriveLocalMobile'}
                            defaultMessage={'Arrival (local)'}
                        />
                    }
                >
                    <LocalTimeWithFallback dateTime={leg.arrivalDate} />
                </LabeledItem>
            }
            {shouldShowFlightDurationFor(leg) &&
                <LabeledItem
                    className={style.MobileLegInfo}
                    label={
                        <FormattedMessage
                            id={'components.legs.flightDetailsLegTable.durationMobile'}
                            defaultMessage={'Duration'}
                        />
                    }
                >
                    <Duration duration={leg.flightTimeDuration} style={DurationStyle.Long} />
                </LabeledItem>
            }
            {departmentKey !== Department.Cargo &&
                <LabeledItem
                    className={style.MobileLegInfo}
                    label={
                        <FormattedMessage
                            id={'components.legs.flightDetailsLegTable.passengersMobile'}
                            defaultMessage={'Passengers'}
                        />
                    }
                >
                    {leg.numberOfPassengers}
                </LabeledItem>
            }
            {departmentKey === Department.Cargo &&
                <LabeledItem
                    className={style.MobileLegInfo}
                    label={
                        <FormattedMessage
                            id={'components.legs.flightDetailsLegTable.cargoWeightMobile'}
                            defaultMessage={'Cargo Weight'}
                        />
                    }
                >
                    {leg.cargoWeight}
                </LabeledItem>
            }
            {shouldShowPetsColumn &&
                <LabeledItem
                    className={style.MobileLegInfo}
                    label={
                        <FormattedMessage
                            id={'components.legs.flightDetailsLegTable.petsMobile'}
                            defaultMessage={'Pets'}
                        />
                    }
                >
                    {leg.numberOfPets}
                </LabeledItem>
            }
        </div>
    );
}
const FlightDetailsLegTable = injectIntl(FlightDetailsLegTablePresentational);

export { FlightDetailsLegTable };
